'use client';
import { useEffect, useRef, useState } from 'react';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const GREETING: Message = {
  role: 'assistant',
  content: 'Olá! Sou o assistente virtual da EnQ Digital. Posso ajudar com planos, preços ou tirar dúvidas sobre nosso Data Center Tier 3.'
};

export default function AIChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([GREETING]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    const openChat = () => setOpen(true);
    window.addEventListener('enq:open-chat', openChat);
    return () => window.removeEventListener('enq:open-chat', openChat);
  }, []);

  async function send() {
    const text = input.trim();
    if (!text || loading) return;
    const next = [...messages, { role: 'user' as const, content: text }];
    setMessages(next);
    setInput('');
    setLoading(true);
    try {
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ messages: next })
      });
      const data = await res.json();
      setMessages((m) => [...m, { role: 'assistant', content: data.reply ?? data.error ?? 'Erro ao responder.' }]);
    } catch {
      setMessages((m) => [...m, { role: 'assistant', content: 'Não consegui responder agora, tente novamente.' }]);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      {open &&
      <div
        role="dialog"
        aria-label="Chat com assistente virtual"
        style={{
          position: 'fixed',
          right: '20px',
          bottom: '88px',
          zIndex: 61,
          width: 'min(340px, calc(100vw - 40px))',
          height: '460px',
          maxHeight: 'calc(100vh - 140px)',
          background: '#0d1117',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: '12px',
          boxShadow: '0 12px 32px rgba(0,0,0,0.4)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>

          <div
            style={{
              padding: '14px 16px',
              borderBottom: '1px solid rgba(255,255,255,0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>

            <span style={{ color: '#fff', fontSize: '14px', fontWeight: 600 }}>Assistente EnQ Digital</span>
            <button
              onClick={() => setOpen(false)}
              aria-label="Fechar chat"
              style={{ color: '#8b98a8', background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', lineHeight: 1 }}>

              ×
            </button>
          </div>

          <div ref={listRef} style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {messages.map((m, i) =>
          <div
            key={i}
            style={{
              alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: '85%',
              background: m.role === 'user' ? '#1a9fff' : 'rgba(255,255,255,0.08)',
              color: '#fff',
              padding: '8px 12px',
              borderRadius: '10px',
              fontSize: '13.5px',
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap'
            }}>

                {m.content}
              </div>
          )}
            {loading &&
          <div style={{ alignSelf: 'flex-start', color: '#8b98a8', fontSize: '13px' }}>digitando…</div>
          }
          </div>

          <div style={{ padding: '12px', borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', gap: '8px' }}>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {if (e.key === 'Enter') send();}}
              placeholder="Digite sua pergunta…"
              aria-label="Mensagem"
              style={{
                flex: 1,
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.14)',
                borderRadius: '8px',
                padding: '9px 12px',
                color: '#fff',
                fontSize: '13.5px',
                outline: 'none'
              }} />

            <button
              onClick={send}
              disabled={loading}
              aria-label="Enviar"
              style={{
                background: '#1a9fff',
                border: 'none',
                borderRadius: '8px',
                width: '38px',
                color: '#fff',
                cursor: loading ? 'default' : 'pointer',
                opacity: loading ? 0.6 : 1
              }}>

              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ margin: '0 auto' }}>
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      }

      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Fechar chat' : 'Abrir chat com assistente virtual'}
        style={{
          position: 'fixed',
          right: '88px',
          bottom: '20px',
          zIndex: 60,
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          background: '#1a9fff',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 14px rgba(0,0,0,0.25)'
        }}>

        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
        </svg>
      </button>
    </>);

}
